/**
 * ImpersonationBanner — top-of-screen ribbon shown only inside an
 * impersonation tab (token lives in sessionStorage). Lets the impersonator
 * exit cleanly back to a fresh login on this tab.
 *
 * Renders two variants based on the impersonated user's role:
 *   - TENANT_ADMIN → "Super-admin preview · {tenant}"   (amber)
 *   - RESTAURANT_OWNER → "Tenant preview · {owner name}" (sky)
 */
import { Eye, LogOut } from "lucide-react";
import { tokenStore } from "../lib/tokenStore";
import { useAuth } from "../contexts/AuthContext";

export default function ImpersonationBanner() {
    const { user, tenant } = useAuth();
    if (!tokenStore.isImpersonation()) return null;

    const isOwner = user?.role === "RESTAURANT_OWNER";
    const label = isOwner
        ? `Tenant preview · ${user?.first_name || "merchant"}${user?.last_name ? " " + user.last_name : ""}`
        : `Super-admin preview · ${tenant?.name || "tenant"}`;
    const wrapCls = isOwner
        ? "bg-sky-500 text-sky-950"
        : "bg-amber-500 text-amber-950";
    const btnCls = isOwner
        ? "bg-sky-950 text-sky-50"
        : "bg-amber-950 text-amber-50";

    const exit = () => {
        tokenStore.clear();
        if (typeof window !== "undefined") {
            window.sessionStorage.removeItem("bizil_tenant_slug");
            window.location.href = "/login";
        }
    };
    return (
        <div data-testid="impersonation-banner" className={`${wrapCls} text-sm font-bold px-4 py-2 flex items-center justify-center gap-3 sticky top-0 z-50`}>
            <Eye size={14} />
            <span>{label}</span>
            <button onClick={exit} data-testid="exit-impersonation" className={`ml-2 inline-flex items-center gap-1 ${btnCls} px-2.5 py-0.5 rounded-full text-xs`}>
                <LogOut size={11} /> Exit preview
            </button>
        </div>
    );
}
